/**
 * The navigation manifest: keys, labels and grouping, with no components attached.
 *
 * Pure on purpose. `src/lib/navAccess.ts` maps each key to the screen permission that opens it,
 * and a test asserts the two lists agree - which needs the manifest to be importable without
 * pulling in React. `src/pages/pages.tsx` attaches a component to each key.
 */
export interface NavItem {
  key: string;
  label: string;
  group: string;
  /** A heading inside the group, above this item and the ones after it with the same subgroup. */
  subgroup?: string;
  /**
   * A lucide icon NAME, not the component. This module stays free of React so
   * `tests/session.test.ts` can import it under `node --test`, where a react import would
   * fail; `src/components/navIcons.tsx` maps the name to the component.
   *
   * As in Hub: siblings never share an icon. They sit side by side, so a repeat makes the
   * glyph meaningless. `navIcons.test.ts` pins that.
   */
  icon: string;
}

export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', group: 'Dashboard' , icon: 'LayoutDashboard' },

  // "Farmer Groups", not "Farmer management" (owner, 2026-09-27), and the plots sit with the farmers
  // who hold them: a plot is a farmer's before it is anything to map.
  { key: 'farmers', label: 'Farmer register', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'UsersRound' },
  { key: 'plots', label: 'Plots', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'MapPin' },
  // Consent is taken per plot (owner, 2026-09-27: "We generally obtain consent from farmers per
  // plot", "separate page is good").
  { key: 'plot-consents', label: 'Plot consents', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'BadgeCheck' },
  // In Onboarding, a register of its own (owner, 2026-09-27: "we can move risk assessment to on-boarding?").
  { key: 'risk-assessment', label: 'Risk assessment', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'ShieldAlert' },
  // 'name-verifications' came off on 2026-09-27 (owner: "we are concerned only with the KYC and land
  // ownership docs"): a name check is decided where its document lives - the plot's Name check tab
  // and the farmer's KYC tab.
  { key: 'references', label: 'References', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'Share2' },
  // Earth View and the tree inventory sit with the farmers and their plots (owner, 2026-09-27:
  // "shift to Farmers Group: Earth View, Tree inventory"). Earth View is every boundary and house
  // on imagery, read-only, with a summary on click.
  { key: 'plot-map', label: 'Earth View', group: 'Farmer Groups', subgroup: 'Onboarding' , icon: 'Map' },

  // Inside Farmer Groups, under a heading of its own (owner, 2026-09-27: "Plot Management shall be
  // moved to Farmers Group", "shift farm management under Farmer Groups?").
  // "Farm Management", not "Plot management" (owner, 2026-09-27): the work done on the farm and
  // what it earned. The farmer statement is the P&L sheet here.
  { key: 'statement', label: 'P&L sheet', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'ReceiptIndianRupee' },
  // Under Farm Management (owner, 2026-09-27: "Tree inventory -- can be shifted to farm management").
  { key: 'inventory', label: 'Tree inventory', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'Boxes' },
  // The six practice registers (owner: "these all make as ui pages under Plot management", now Farm
  // Management). Each
  // was a collapsed section inside one plot and is still written there; these are the same records
  // read across every plot, which is the only way to answer a question an auditor actually asks.
  { key: 'silviculture', label: 'Silviculture activities', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'Scissors' },
  { key: 'inputs', label: 'Inputs applied', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'SprayCan' },
  { key: 'irrigation', label: 'Irrigation', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'Droplets' },
  { key: 'soil-tests', label: 'Soil test reports', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'FlaskConical' },
  { key: 'pests', label: 'Pests & diseases', group: 'Farmer Groups', subgroup: 'Farm Management' , icon: 'Bug' },
  // Harvest to sale, in the order it happens (owner, 2026-09-27: "we create a seperate tab under
  // farmers: Harvest Permits (Internal), Harvest Report (Internal), FD Permits, Sale Invoices, sale
  // dockets?", "all under sales?"). The organisation's own trade stays on Sale & purchase.
  { key: 'internal-permits', label: 'Harvest permits (internal)', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'FileSignature' },
  { key: 'harvest-permits', label: 'FD permits', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'Stamp' },
  { key: 'harvest-report', label: 'Harvest report', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'ClipboardCheck' },
  // The harvest package (owner, 2026-09-27): "Harvest activity workers - summary page as similar to
  // silviculture", "Yield Statement summary (some are based on tonnage, some on M3 - log wise)".
  { key: 'harvest-labour', label: 'Harvest labour', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'Pickaxe' },
  { key: 'yield-statement', label: 'Yield statement', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'Scale' },
  { key: 'farmer-sales', label: 'Farmer sales', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'HandCoins' },
  { key: 'sale-invoices', label: 'Sale invoices', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'Receipt' },
  { key: 'sale-dockets', label: 'Sale dockets', group: 'Farmer Groups', subgroup: 'Sales' , icon: 'Truck' },
  // "Monitoring aspects", not "Compliance registers", inside Farmer Groups (owner, 2026-09-27:
  // "rename as monitoring aspects, shift under farmers groups").
  // Saplings out, and what became of them (owner, 2026-09-25: "Add a sapling issue record",
  // "Add a tree monitoring record", "both are under compliance registers?"). Tree monitoring came
  // out of Plot management on the same day: the owner saw it was the same walk as a survival
  // check - "duplicate, can be single and merged" - so the two are one register, and it belongs
  // beside the evidence it produces rather than beside the plot it happens on.
  { key: 'sapling-issues', label: 'Sapling issue', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'Sprout' },
  { key: 'growth', label: 'Tree monitoring', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'Ruler' },
  { key: 'satellite', label: 'Satellite monitoring', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'Satellite' },
  // Did anyone see the alert, and is the worker draining the queue (2026-09-27)? The views and the
  // acknowledge function were live with nothing reading them.
  { key: 'satellite-ops', label: 'Satellite alerts & jobs', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'RadioTower' },
  { key: 'evidence-packs', label: 'Evidence packs', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'FileCheck2' },
  { key: 'renewals', label: 'Renewals due', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'CalendarClock' },
  { key: 'annual-monitoring', label: 'Annual monitoring', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'ClipboardList' },
  { key: 'internal-audits', label: 'Internal audits', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'SearchCheck' },
  { key: 'consultations', label: 'Stakeholder consultations', group: 'Farmer Groups', subgroup: 'Monitoring aspects' , icon: 'MessagesSquare' },
  // Every asset and risk marked on the farms' maps, in one register (owner, 2026-09-27: "farm risk
  // assessment of assets viz. HCV or some thing is missing?").

  { key: 'kyc', label: 'Party KYC', group: 'Parties & consent' , icon: 'BadgeCheck' },
  // Consents and withdrawals on one page (owner, 2026-09-25: "consent capture and withdrawl can
  // be made in to single page"). They were already the same screen - `ConsentScreen` has shown
  // the withdrawal register as its second tab since it was built, and `WithdrawalsScreen` was a
  // wrapper around the identical component. Two doors onto one register is how two doors start
  // disagreeing.
  { key: 'consent-capture', label: 'Consent & withdrawal', group: 'Parties & consent' , icon: 'ShieldCheck' },
  { key: 'stakeholders', label: 'Stakeholders', group: 'Parties & consent' , icon: 'Users2' },
  // Moved out of plots on 2026-09-25 (owner: "work sites and contractors, go to the parties
  // section"). Both are parties the organisation deals with, not places it maps: a contractor
  // signs a contract and a work site belongs to one. `workers` stayed with the plots, where the
  // work is recorded - say the word and it follows them.
  { key: 'sites', label: 'Work sites', group: 'Parties & consent' , icon: 'Factory' },
  { key: 'contractors', label: 'Contractors', group: 'Parties & consent' , icon: 'HardHat' },
  // Under the contractors who engage them (owner, 2026-09-27: "Workers can be shifted below contractors?").
  { key: 'workers', label: 'Workers', group: 'Parties & consent' , icon: 'Pickaxe' },

  // The whole chain on one screen (owner, 2026-09-26: "first build ecosystem screen managment for
  // the client admin?"). It sits AFTER the registers it summarises rather than replacing them:
  // this answers "who is in my chain and who needs chasing", and each register is still where the
  // record itself is edited.
  { key: 'ecosystem', label: 'Ecosystem', group: 'Parties & consent' , icon: 'Network' },
  { key: 'engagements', label: 'Party engagements', group: 'Parties & consent', icon: 'Handshake' },

  // "Plot management", not "Plots & mapping" (owner, 2026-09-25: "rename mapping"): mapping is one
  // thing done to a plot, and by now the smaller part of what this group holds.
  // 'plots' moved to Farmer Groups on 2026-09-27 (owner: "Shift plots and tenure to 'Farmer Groups'
  // and rename as Plots").

  // A main section of its own (owner, 2026-09-25: "Sale and purchase go to a seperate main
  // section"). It sat under the plots because a sale usually starts from a harvest on one, but
  // what it records is a transaction between two parties - an invoice, a payment, an FSC claim
  // passing from a seller to a buyer - and that outlives the plot it came off. It is also the
  // only screen here that a buyer, not a field officer, asks questions of.
  { key: 'trades', label: 'Sale & purchase', group: 'Trade' , icon: 'ArrowLeftRight' },

  // Seasoning kilns and chemical tanks (owner, 2026-09-26: "Add seasoning chamber and its
  // management"; 2026-09-27: "create all ui aspects ... add to sidebar"). A group of its own because
  // what happens here - drying and treating sawn timber - is neither a plot nor a trade, and it is
  // where the chain map's processing steps will sit.
  { key: 'chambers', label: 'Seasoning chambers', group: 'Processing' , icon: 'Heater' },
  { key: 'chamber-norms', label: 'Seasoning norms', group: 'Processing' , icon: 'Timer' },
  { key: 'chamber-loads', label: 'Chamber loads', group: 'Processing', icon: 'Boxes' },
  { key: 'treatment-chemicals', label: 'Treatment chemicals', group: 'Processing', icon: 'FlaskConical' },
  { key: 'process-runs', label: 'Process runs', group: 'Processing', icon: 'GitBranch' },


  // Organisation-wide, not per farmer (owner, 2026-09-27: "accident and compliant registers are
  // under global level"): their own group, outside Farmer Groups.
  { key: 'complaints', label: 'Complaint register', group: 'Compliance registers' , icon: 'MessageSquareWarning' },
  { key: 'accidents', label: 'Accident register', group: 'Compliance registers' , icon: 'Siren' },

  { key: 'inbox', label: 'Approval inbox', group: 'Activity & approvals' , icon: 'Inbox' },
  { key: 'my-alerts', label: 'My alerts', group: 'Activity & approvals' , icon: 'BellRing' },
  // Everybody's messages, codes, wording and providers (2026-09-27). Back from the prototypes: it
  // reads the comms views, and switching a provider on is what lets anything leave the platform.
  { key: 'comms', label: 'Communications', group: 'Activity & approvals' , icon: 'Send' },

  { key: 'audit', label: 'Audit timeline', group: 'Audit & readiness' , icon: 'History' },

  { key: 'organisation', label: 'Organisation profile', group: 'Admin & configuration' , icon: 'SlidersHorizontal' },
  // The gate under every place dropdown in the application (owner, 2026-09-25: "out
  // geo-repository of database ui is needed as a seperate page?"). It is admin rather than
  // geography because the question it answers is about the organisation - where do we work -
  // not about a record.
  { key: 'geography', label: 'Geography repository', group: 'Admin & configuration' , icon: 'Globe2' },
  // Moved out of Parties on 2026-09-25 (owner: "shift consent templates to admin and config").
  // A template is approved WORDING - configuration an admin sets up once and legal signs off -
  // not a register of what any farmer agreed to. It sits with the other things that decide how
  // the application behaves.
  // Each translated consent beside its English source, with its legal and language reviews
  // (2026-09-27). Back from the prototypes: PENDING item 14 said the translations were drafts
  // awaiting review, and until today nothing recorded whether review had happened.
  // Every kind of file the organisation stores (2026-09-27). Back from the prototypes: it reads and
  // writes `api.document_types`, and `doc.fn_document_type_guard` keeps the codes the application
  // reads by name.
  { key: 'doctypes', label: 'Document types', group: 'Admin & configuration' , icon: 'FileType2' },
  // Which documents each kind of party must hold (2026-09-27). PENDING item 7 said the rule was
  // the organisation's to decide; until today nothing in the application could record it.
  { key: 'kyc-requirements', label: 'KYC requirements', group: 'Admin & configuration' , icon: 'ListChecks' },
  // Who approves what (2026-09-27). Back from the prototypes: the approval engine reads these
  // templates and steps, and the screen edits them through api.workflow_templates / _steps.
  { key: 'workflows', label: 'Approval workflows', group: 'Admin & configuration' , icon: 'GitBranch' },
  // The lists every dropdown reads (2026-09-27). Back from the prototypes: it reads
  // api.reference_values and words them through api.fn_update_reference_value.
  { key: 'config', label: 'Reference data', group: 'Admin & configuration' , icon: 'Database' },
  // What a Bigha is in hectares, per state (2026-09-27). Application-wide, kept by the app owner;
  // it used to sit on the Organisation page as if each organisation set its own.
  { key: 'land-units', label: 'Land units', group: 'Admin & configuration' , icon: 'Ruler' },
  // Owner, 2026-09-27: "create HSN Database and connect (as required for all parties)". App-wide.
  { key: 'hsn-codes', label: 'HSN codes', group: 'Admin & configuration' , icon: 'Receipt' },
  { key: 'organisations', label: 'Organisations', group: 'Admin & configuration' , icon: 'Building2' },
  { key: 'users', label: 'Users & devices', group: 'Admin & configuration' , icon: 'Users' },
  { key: 'onboarding', label: 'Onboarding', group: 'Admin & configuration' , icon: 'UserPlus' },
  { key: 'access', label: 'Access & scope', group: 'Admin & configuration' , icon: 'KeySquare' },
  // What "Team" record scope is worked out from (2026-09-27). Until today nothing could create a
  // team or put anyone in one, so "Team" quietly meant "Own" in every organisation but the pilot.
  { key: 'teams', label: 'Teams & departments', group: 'Admin & configuration' , icon: 'UsersRound' },
];

export const NAV_KEYS: string[] = NAV_ITEMS.map((item) => item.key);

/**
 * Taken OUT of the menu on 2026-09-25 (owner: "drop those 14 from the menu").
 *
 * Each of these opened a static prototype - a `*Page` in `src/pages/`, not a `*Screen`. Between
 * them those files make no database call at all: Geography scope named West Bengal, Darjeeling and
 * Mirik while the real data is Karnataka, and their toolbar offered "Universal Search" and "Filter"
 * buttons with no handler behind them. An auditor cannot tell a mock-up from a register, so a
 * mock-up does not belong beside one.
 *
 * Nothing was deleted. `NAV_COMPONENTS` in `src/pages/pages.tsx` still maps every one of these
 * keys, so bringing one back is adding its line to NAV_ITEMS above - which is exactly what should
 * happen once the screen is real and reads from the database. Until then `navIcons.test.ts` keeps
 * it out, and its route is gone with it: the shell builds routes from NAV_ITEMS, so an old
 * bookmark lands on the user's first permitted screen instead.
 *
 * The screen permissions they used (`document.library`, `comms.log`, `reference.data`,
 * `geography.activation`, `workflow.configuration`, `document.type.master`, `farmer.statement`)
 * still exist in the database and roles may still hold them. Nothing in the interface reads them
 * now.
 */
export const PROTOTYPE_KEYS: string[] = [
  // 'statement' came off this list on 2026-09-25: it is a real screen now, reading
  // `api.farmer_financial_entries` and `api.fn_farmer_statement`.
  // 'geography' came off the same day, for the same reason. The prototype showed West Bengal,
  // Darjeeling and Mirik whatever the database held; `GeographyScreen` reads the master through
  // `api.fn_geography_repository` and writes through `api.fn_set_geography_activation`. Twelve
  // to go.
  // 'doctypes' came off on 2026-09-27: `DocumentTypesScreen` reads and writes `api.document_types`.
  //
  // Decided on 2026-09-27, and kept on this list only so the menu test keeps them out:
  //   'approvals'  - RETIRED. Its subject (who approves, self-approval, lock rules) is Approval
  //                  workflows and the Approval inbox, both live.
  //   'activities' - RETIRED. "Who did what, for which farmer and plot" is the Audit timeline, which
  //                  reads the real change log instead of four invented rows.
  //   'crud'       - RETIRED. A local IndexedDB bench from before the database existed; every
  //                  register now has real create, edit and delete.
  //   'scans'      - PARKED. OCR has no backend at all and needs a provider chosen by the owner.
  //   'offline'    - PARKED. Offline sync waits on PENDING item 17.
  // Still to build: 'share-wizard', 'sharing'. ('translations' came off on 2026-09-27.)
  'scans', 'share-wizard', 'sharing',
  'approvals', 'activities',
  // 'comms' came off on 2026-09-27: `CommsScreen` reads the message and code logs and keeps the providers.
  'offline',
  // 'workflows' came off on 2026-09-27: `WorkflowsScreen` edits the templates the approval engine reads.
  // 'config' came off on 2026-09-27: `ReferenceDataScreen` reads and words the reference lists.
  'crud',
];

/**
 * One icon per group, for the rail (owner, 2026-09-21: "left side menu as like our hub side menu"):
 * collapsed, each group is a single icon whose screens fly out on hover. Names, not components,
 * for the same reason as the items above; no two groups share one.
 */
export const NAV_GROUP_ICONS: Record<string, string> = {
  'Dashboard': 'LayoutDashboard',
  'Admin & configuration': 'Settings',
  'Parties & consent': 'Handshake',
  'Farmer Groups': 'Sprout',
  // Unused while Trade holds one screen: `SideNav` flattens a single-item group to the item
  // itself, label, icon and all, exactly as it already does for Dashboard and Audit timeline -
  // which is why nobody ever sees the word "Trade". Here for the day a second trade screen
  // arrives, because without it that group would fall back to the dashboard glyph.
  'Trade': 'ArrowLeftRight',
  'Compliance registers': 'ClipboardCheck',
  // The subgroups inside Farmer Groups, drawn as a row that flies out.
  // "Onboarding" (owner, 2026-09-27: "Group this under 'Onboarding'"): the farmer, their plots,
  // who referred them and where they are.
  'Onboarding': 'UserPlus',
  'Farm Management': 'Trees',
  'Monitoring aspects': 'SearchCheck',
  'Sales': 'BadgeIndianRupee',
  'Processing': 'Factory',
  'Activity & approvals': 'CheckSquare',
  'Audit & readiness': 'ShieldCheck',
};
